import { useEffect, useState } from 'react';
import { isDefaultDoctorProfileImage } from '@/modules/profile/utils/doctorProfileImageUrl';
import { pickHeadImage } from '@/modules/profile/utils/progressivePage';

/**
 * Avatar URL for the progressive header (see `pickHeadImage`). Preloads a profile picture that
 * differs from the search seed off-screen and swaps only after it loaded. `enabled` false (SSR
 * render) → `finalUrl` unchanged.
 */
export function useProgressiveHeadImage(p: {
  enabled: boolean;
  seedImage?: string;
  finalUrl?: string;
  /** every profile piece settled */
  complete: boolean;
}): string | undefined {
  const { enabled, seedImage, finalUrl, complete } = p;
  const finalKnown = complete || (!!finalUrl && !isDefaultDoctorProfileImage(finalUrl));
  const [loaded, setLoaded] = useState<string | null>(null);
  const url = pickHeadImage({ seedImage, finalUrl, finalKnown, finalLoaded: false });
  const wantsSwap = enabled && finalKnown && !!finalUrl && !!seedImage && url === seedImage && url !== finalUrl;
  const swapCandidate = wantsSwap && pickHeadImage({ seedImage, finalUrl, finalKnown, finalLoaded: true }) === finalUrl;

  useEffect(() => {
    if (!swapCandidate || !finalUrl || loaded === finalUrl) return;
    const img = new Image();
    let live = true;
    const done = () => live && setLoaded(finalUrl);
    img.onload = done;
    // A broken new URL: keep the seed rather than an empty/broken avatar.
    img.src = finalUrl;
    return () => {
      live = false;
      img.onload = null;
    };
  }, [swapCandidate, finalUrl, loaded]);

  if (!enabled) return finalUrl;
  return pickHeadImage({ seedImage, finalUrl, finalKnown, finalLoaded: loaded === finalUrl });
}
